import { useLocalization } from "@fluent/react";
import { Check } from "react-feather";
import {
  useLocation,
  useNavigate,
  useParams,
  type NavigateFunction,
} from "react-router-dom";
import type OrganizationUser from "byzantine/src/OrganizationUser";

import OrganizationUserList, {
  DISPLAY_FILTERS,
  FILTER,
  determineSortIcon,
} from "byzantine/src/OrganizationUserList";

import {
  Alert,
  Button,
  Chip,
  Popover,
  TextInput,
} from "@narmi/design_system";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import { useOrganizationUserContext } from "byzantine/src/OrganizationUser/OrganizationUserProvider";
import { useTimedBannerVisibility } from "./useTimedBannerVisibility";

import { InstitutionSettingsContextProvider } from "../contexts/InstitutionSettingsContext";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import OrganizationUsersTable from "./OrganizationUsersTable";
import OrganizationUserInfoDrawer from "./OrganizationUserInfoDrawer";
import { FilterChip } from "./FilterChip";

import type { FilterFunction, SortFunction } from "./OrganizationUsers.types";

const getFilterCount = (
  filters: FilterFunction[],
  displayFilters: any[]
): number | undefined => {
  const count = filters.filter((f) =>
    displayFilters.some((df) => df.filter === f)
  ).length;
  return count || undefined;
};

export function Header({
  search,
  setSearch,
  filters,
  toggleFilter,
  navigate,
  unassignedUserCount,
}: {
  search: string;
  setSearch: (value: string) => void;
  filters: FilterFunction[];
  toggleFilter: (filter: FilterFunction) => void;
  navigate: NavigateFunction;
  unassignedUserCount: number;
}) {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const { visible, dismiss } = useTimedBannerVisibility(
    currentUser?.id ?? "",
    unassignedUserCount > 0
  );
  const [filteringByRole, setFilteringByRole] = useState(false);
  const [filteringByStatus, setFilteringByStatus] = useState(false);
  const [isRolePopoverOpen, setIsRolePopoverOpen] = useState(false);
  const [isStatusPopoverOpen, setIsStatusPopoverOpen] = useState(false);
  const [pendingRoleFilters, setPendingRoleFilters] = useState<
    FilterFunction[]
  >([]);
  const [pendingStatusFilters, setPendingStatusFilters] = useState<
    FilterFunction[]
  >([]);

  const roleDisplayFilters =
    unassignedUserCount > 0
      ? [
          ...DISPLAY_FILTERS.ROLE,
          { text: "Unassigned", filter: FILTER.ROLE_UNASSIGNED },
        ]
      : DISPLAY_FILTERS.ROLE;

  useEffect(() => {
    if (isRolePopoverOpen) {
      setPendingRoleFilters(
        filters.filter((f) => roleDisplayFilters.some((rf) => rf.filter === f))
      );
    }
  }, [isRolePopoverOpen, filters]);

  useEffect(() => {
    if (isStatusPopoverOpen) {
      setPendingStatusFilters(
        filters.filter((f) =>
          DISPLAY_FILTERS.STATUS.some((sf) => sf.filter === f)
        )
      );
    }
  }, [isStatusPopoverOpen, filters]);

  const SearchInput = (
    <TextInput
      startIcon="search"
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        setSearch(event.target.value)
      }
      placeholder={l10n.getString("manage-users-search-by-name-or-email")}
      value={search}
    />
  );

  const ChipFilters = () => {
    const PopoverContent = () => (
      <div style={{ padding: "8px" }}>
        <div
          style={{
            width: "138px",
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <div
            role="button"
            tabIndex={0}
            onClick={() => setFilteringByRole(!filteringByRole)}
            onKeyUp={({ key }) => {
              if (key === "Enter") {
                setFilteringByRole(!filteringByRole);
              }
            }}
            style={{
              padding: "12px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{l10n.getString("org-user-filter-role")}</span>
            {filteringByRole && <Check size={16} />}
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setFilteringByStatus(!filteringByStatus)}
            onKeyUp={({ key }) => {
              if (key === "Enter") {
                setFilteringByStatus(!filteringByStatus);
              }
            }}
            style={{
              padding: "12px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{l10n.getString("org-user-filter-status")}</span>
            {filteringByStatus && <Check size={16} />}
          </div>
        </div>
      </div>
    );

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "16px",
          width: "fit-content",
        }}
       >
         {filteringByRole && (
           <FilterChip
             displayFilters={roleDisplayFilters}
             pendingFilters={pendingRoleFilters}
             setPendingFilters={setPendingRoleFilters}
             filterType="ROLE"
             isOpen={isRolePopoverOpen}
             setIsOpen={setIsRolePopoverOpen}
             label={l10n.getString("org-user-filter-role")}
             filters={filters}
             toggleFilter={toggleFilter}
             setFilteringByRole={setFilteringByRole}
             setFilteringByStatus={setFilteringByStatus}
             l10n={l10n}
             getFilterCount={getFilterCount}
           />
         )}
         {filteringByStatus && (
           <FilterChip
             displayFilters={DISPLAY_FILTERS.STATUS}
             pendingFilters={pendingStatusFilters}
             setPendingFilters={setPendingStatusFilters}
             filterType="STATUS"
             isOpen={isStatusPopoverOpen}
             setIsOpen={setIsStatusPopoverOpen}
             label={l10n.getString("org-user-filter-status")}
             filters={filters}
             toggleFilter={toggleFilter}
             setFilteringByRole={setFilteringByRole}
             setFilteringByStatus={setFilteringByStatus}
             l10n={l10n}
             getFilterCount={getFilterCount}
           />
         )}
        <div
          className="org-users-left-aligned-popover"
          style={{ display: "inline-block" }}
        >
          <Popover
            content={<PopoverContent />}
            wrapperDisplay="inline"
          >
        <Chip
          kind="secondary"
          startIcon="filter-2"
          isLabelVisible={false}
          label=""
        />
          </Popover>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="padding--top--l" style={{ marginBottom: "var(--space-l)" }}>
      <Alert 
        isActive={visible}
        isDismissable
        onUserDismiss={dismiss}
      >
          <p className="margin--none">
            {l10n.getString("org-user-unassigned-warning", {
              count: unassignedUserCount,
            })}
          </p>
          <Button 
            kind="plain"
            onClick={() => {
              toggleFilter(FILTER.ROLE_UNASSIGNED);
              setFilteringByRole(true);
              dismiss();
            }}
          >
           {l10n.getString("org-user-unassigned-button")} 
          </Button>
        </Alert>
      <div
        className="mobile-only"
        style={{ marginBottom: "var(--space-m)", marginTop: "var(--space-s)"}}
      >
        <Button
          kind="primary"
          startIcon="plus"
          label={l10n.getString("button-add-new-user")}
          size="xs"
          onClick={() => navigate("/new")}
        />
      </div>
      </div>
      <div className="searchAndFilterContainer">
        <div
          className="search inputBox nds-input-style-override"
          data-testid="input-box"
        >
          {SearchInput}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "16px",
        }}
      >
        {ChipFilters()}
        <div className="mobile-hidden">
          <Button
            kind="primary"
            startIcon="plus"
            label={l10n.getString("button-add-new-user")}
            size="xs"
            onClick={() => navigate("/new")}
          />
        </div>
      </div>
    </div>
  );
}

type ContainerProps = {
  organizationUsers: OrganizationUser[];
  displayOrganizationUsers: OrganizationUser[];
  search: string;
  setSearch: (value: string) => void;
  filters: FilterFunction[];
  toggleFilter: (filter: FilterFunction) => void;
  sortOrder: SortFunction;
  setSortOrder: (updater: (prev: SortFunction) => SortFunction) => void;
  determineSortIcon: (sortType: string, sortOrder: SortFunction) => string;
  onUserUpdated?: () => void;
  currentOrganizationUser: OrganizationUser | null;
  navigate: NavigateFunction;
};

export const Container = (props: ContainerProps) => {
  const { l10n } = useLocalization();
  const location = useLocation();
  const params = useParams();
  const selectedUserUuid = params.userUuid ?? null;
  const selectedUser = useMemo(
    () =>
      props.organizationUsers.find((user) => user.uuid === selectedUserUuid) ||
      null,
    [props.organizationUsers, selectedUserUuid]
  );
  const selectedIndex = useMemo(
    () =>
      props.displayOrganizationUsers.findIndex(
        (user) => user.uuid === selectedUserUuid
      ),
    [props.displayOrganizationUsers, selectedUserUuid]
  );
  const isDrawerOpen = Boolean(selectedUserUuid && selectedUser);

  const updateSelectedUser = useCallback(
    (userUuid: string | null) => {
      props.navigate(
        userUuid
          ? { pathname: `/${userUuid}`, search: location.search }
          : { pathname: "/", search: location.search },
        { replace: true }
      );
    },
    [location.search, props.navigate]
  );

  const handleRowClick = (user: OrganizationUser) => {
    updateSelectedUser(user.uuid);
  };

  const handleOnPrev =
    selectedIndex > 0
      ? () => updateSelectedUser(props.displayOrganizationUsers[selectedIndex - 1].uuid)
      : undefined;

  const handleOnNext =
    selectedIndex !== -1 &&
    selectedIndex < props.displayOrganizationUsers.length - 1
      ? () => updateSelectedUser(props.displayOrganizationUsers[selectedIndex + 1].uuid)
      : undefined;

  useEffect(() => {
    if (selectedUserUuid && !selectedUser) {
      updateSelectedUser(null);
    }
  }, [selectedUser, selectedUserUuid, updateSelectedUser]);

  const unassignedUserCount = props.organizationUsers.filter(
    (user) => user.role.isUnassigned()
  ).length;

  const NoResults = () => {
    const noResultsStyle = {
      backgroundColor: "rgb(var(--background-grey-color))",
      padding: "32px 0px 0px 4px",
    };
    const largerTextStyle = {
      color: "black",
      fontSize: "var(--font-size-l)",
      lineHeight: "100%",
      fontWeight: "bold",
      paddingBottom: "8px",
      marginBottom: "0px",
    };
    const smallerTextStyle = {
      fontSize: "var(--font-size-m)",
      lineHeight: "100%",
    };

    return (
      <div style={noResultsStyle}>
        <p style={largerTextStyle}>
          {l10n.getString("manage-users-no-users-found")}
        </p>
        <p style={smallerTextStyle}>
          {l10n.getString("manage-users-adjust-your-filters")}
        </p>
      </div>
    );
  };

  const contents =
    props.displayOrganizationUsers.length === 0 ? (
      <NoResults />
    ) : (
        <OrganizationUsersTable
          organizationUsers={props.organizationUsers}
          displayOrganizationUsers={props.displayOrganizationUsers}
          sortOrder={props.sortOrder}
          setSortOrder={props.setSortOrder}
          determineSortIcon={props.determineSortIcon}
          onRowClick={handleRowClick}
        />
     );
  return (
    <div className="OrganizationUserList">
      <Header
        search={props.search}
        setSearch={props.setSearch}
        filters={props.filters}
        toggleFilter={props.toggleFilter}
        navigate={props.navigate}
        unassignedUserCount={unassignedUserCount}
      />
      <div className="padding--top--xl">{contents}</div>
      <OrganizationUserInfoDrawer
        isOpen={isDrawerOpen}
        selectedUser={selectedUser}
        onUserDismiss={() => updateSelectedUser(null)}
        currentOrganizationUser={props.currentOrganizationUser}
        onNext={handleOnNext}
        onPrev={handleOnPrev}
      />
    </div>
  );
};

export default function OrganizationUsers({
  currentOrganizationUser,
}: {
  onUserUpdated?: () => void;
  currentOrganizationUser: OrganizationUser | null;
}) {
  const navigate = useNavigate();
  const { isLoading, organizationUsers } = useOrganizationUserContext();

  if (isLoading) return null;
  return (
    <InstitutionSettingsContextProvider>
      <div style={{ minHeight: "800px" }}>
        <OrganizationUserList
          organizationUsers={organizationUsers}
          currentOrganizationUser={currentOrganizationUser}
          navigate={navigate}
          renderContainer={Container}
          determineSortIcon={determineSortIcon}
        />
      </div>
    </InstitutionSettingsContextProvider>
  );
}
